<template>
    <div
        :class="[
            isListenerMobile
                ? 'listener-search'
                : 'bg-base-200 h-screen-header rounded-xl md:p-4 overflow-y-auto fade-in'
        ]"
    >
        <template v-if="isListenerMobile">
            <section class="listener-search__hero">
                <div class="listener-search__top">
                    <button class="listener-search__back" @click="goBack">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M15 18l-6-6 6-6"></path>
                        </svg>
                    </button>
                    <div>
                        <div class="listener-search__eyebrow">{{ $tt('Search') }}</div>
                        <h1 class="listener-search__title">{{ $tt('搜索你想听的声音') }}</h1>
                    </div>
                </div>

                <div class="listener-search__bar">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="11" cy="11" r="7"></circle>
                        <path d="m20 20-3.5-3.5"></path>
                    </svg>
                    <input
                        ref="mobileInput"
                        v-model.trim="searchTerm"
                        type="search"
                        :placeholder="$tt('搜索艺术家、专辑或歌曲...')"
                        @input="handleInput"
                        @keyup.enter="submitSearch"
                    >
                    <button v-if="searchTerm" class="listener-search__clear" @click="clearSearch">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.7 2.88 18.3 9.17 12 2.88 5.71 4.29 4.3l6.3 6.29 6.29-6.3z"></path>
                        </svg>
                    </button>
                </div>
            </section>

            <section v-if="isLoading" class="listener-search__state">
                <div class="listener-search__loading"></div>
                <p>{{ $tt('正在搜索...') }}</p>
            </section>

            <section v-else-if="!hasKeyword" class="listener-search__state listener-search__state--idle">
                <div class="listener-search__hint-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                        <circle cx="11" cy="11" r="7"></circle>
                        <path d="m20 20-3.5-3.5"></path>
                    </svg>
                </div>
                <h2>{{ $tt('开始一次搜索') }}</h2>
                <p>{{ $tt('输入歌曲名、专辑名或艺术家，结果会立即出现。') }}</p>
            </section>

            <template v-else>
                <section v-if="artists.length" class="listener-search__section">
                    <div class="listener-search__section-header">
                        <h2>{{ $tt('艺术家') }}</h2>
                        <span>{{ artists.length }}</span>
                    </div>
                    <div class="listener-search__artist-list">
                        <button
                            v-for="artist in artists"
                            :key="artist.id"
                            class="listener-search__artist-card"
                            @click="redirectArtist(artist)"
                        >
                            <img
                                :src="artist.image || '/static/dist/img/album_null.jpg'"
                                :alt="artist.name"
                                class="listener-search__artist-image"
                            >
                            <div class="listener-search__artist-meta">
                                <div class="listener-search__artist-name">{{ artist.name }}</div>
                                <div class="listener-search__artist-sub">
                                    {{ $tt('{count} 张专辑', { count: artist.albumCount || 0 }) }}
                                </div>
                            </div>
                        </button>
                    </div>
                </section>

                <section v-if="albums.length" class="listener-search__section">
                    <div class="listener-search__section-header">
                        <h2>{{ $tt('专辑') }}</h2>
                        <span>{{ albums.length }}</span>
                    </div>
                    <div class="listener-search__album-grid">
                        <button
                            v-for="album in albums"
                            :key="album.id"
                            class="listener-search__album-card"
                            @click="redirectAlbum(album)"
                        >
                            <img
                                :src="album.image || '/static/dist/img/album_null.jpg'"
                                :alt="album.name"
                                class="listener-search__album-image"
                            >
                            <div class="listener-search__album-title">{{ album.name }}</div>
                            <div class="listener-search__album-sub">{{ album.artist }}</div>
                        </button>
                    </div>
                </section>

                <section v-if="tracks.length" class="listener-search__section">
                    <div class="listener-search__section-header">
                        <h2>{{ $tt('歌曲') }}</h2>
                        <span>{{ tracks.length }}</span>
                    </div>
                    <div class="listener-search__track-list">
                        <button
                            v-for="(track, index) in tracks"
                            :key="track.id || `track-${index}`"
                            class="listener-search__track-row"
                            @click="playTrack(index)"
                        >
                            <img
                                :src="track.image || '/static/dist/img/album_null.jpg'"
                                :alt="track.title"
                                class="listener-search__track-cover"
                            >
                            <div class="listener-search__track-meta">
                                <div class="listener-search__track-title">{{ track.title }}</div>
                                <div class="listener-search__track-sub">{{ track.artist }}<span v-if="track.album"> · {{ track.album }}</span></div>
                            </div>
                            <div class="listener-search__track-duration">{{ formatDuration(track.duration) }}</div>
                        </button>
                    </div>
                </section>

                <section v-if="showEmpty" class="listener-search__state listener-search__state--empty">
                    <div class="listener-search__hint-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                            <circle cx="11" cy="11" r="7"></circle>
                            <path d="m20 20-3.5-3.5"></path>
                        </svg>
                    </div>
                    <h2>{{ $tt('没有找到结果') }}</h2>
                    <p>{{ $tt('换个关键词试试，比如歌手名或者专辑名。') }}</p>
                </section>
            </template>
        </template>

        <template v-else>
            <div class="p-4 md:p-6">
                <div class="flex items-center justify-between gap-4 mb-6">
                    <div>
                        <div class="text-sm font-bold text-base-content/50 uppercase tracking-wider">{{ $tt('Search') }}</div>
                        <h1 class="text-3xl font-bold text-base-content">{{ $tt('音乐搜索') }}</h1>
                    </div>
                </div>

                <div class="bg-base-100 rounded-2xl p-4 md:p-5 shadow-lg mb-6">
                    <div class="relative">
                        <input
                            ref="desktopInput"
                            v-model.trim="searchTerm"
                            type="search"
                            class="input input-bordered w-full pr-12"
                            :placeholder="$tt('搜索艺术家、专辑或歌曲...')"
                            @input="handleInput"
                        >
                        <button
                            v-if="searchTerm"
                            class="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/45 hover:text-base-content"
                            @click="clearSearch"
                        >
                            <svg viewBox="0 0 24 24" class="w-5 h-5" fill="currentColor">
                                <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.7 2.88 18.3 9.17 12 2.88 5.71 4.29 4.3l6.3 6.29 6.29-6.3z"></path>
                            </svg>
                        </button>
                    </div>
                </div>

                <div v-if="isLoading" class="text-center text-base-content/60 py-16">
                    {{ $tt('正在搜索...') }}
                </div>

                <div v-else-if="!hasKeyword" class="text-center text-base-content/60 py-16">
                    {{ $tt('输入关键词后开始搜索') }}
                </div>

                <div v-else-if="showEmpty" class="text-center text-base-content/60 py-16">
                    {{ $tt('未找到相关结果') }}
                </div>

                <div v-else class="space-y-6">
                    <section v-if="artists.length" class="bg-base-100 rounded-2xl p-5 shadow-lg">
                        <div class="flex items-center justify-between mb-4">
                            <h2 class="text-xl font-bold">{{ $tt('艺术家') }}</h2>
                            <span class="text-sm text-base-content/50">{{ artists.length }}</span>
                        </div>
                        <div class="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                            <button
                                v-for="artist in artists"
                                :key="artist.id"
                                class="flex items-center gap-3 p-3 rounded-xl hover:bg-base-200 text-left"
                                @click="redirectArtist(artist)"
                            >
                                <img :src="artist.image || '/static/dist/img/album_null.jpg'" :alt="artist.name" class="w-14 h-14 rounded-full object-cover">
                                <div class="min-w-0">
                                    <div class="font-semibold truncate">{{ artist.name }}</div>
                                    <div class="text-sm text-base-content/60">{{ $tt('{count} 张专辑', { count: artist.albumCount || 0 }) }}</div>
                                </div>
                            </button>
                        </div>
                    </section>

                    <section v-if="albums.length" class="bg-base-100 rounded-2xl p-5 shadow-lg">
                        <div class="flex items-center justify-between mb-4">
                            <h2 class="text-xl font-bold">{{ $tt('专辑') }}</h2>
                            <span class="text-sm text-base-content/50">{{ albums.length }}</span>
                        </div>
                        <div class="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-4">
                            <button
                                v-for="album in albums"
                                :key="album.id"
                                class="text-left"
                                @click="redirectAlbum(album)"
                            >
                                <img :src="album.image || '/static/dist/img/album_null.jpg'" :alt="album.name" class="w-full aspect-square rounded-xl object-cover mb-2">
                                <div class="font-semibold truncate">{{ album.name }}</div>
                                <div class="text-sm text-base-content/60 truncate">{{ album.artist }}</div>
                            </button>
                        </div>
                    </section>

                    <section v-if="tracks.length" class="bg-base-100 rounded-2xl p-5 shadow-lg">
                        <div class="flex items-center justify-between mb-4">
                            <h2 class="text-xl font-bold">{{ $tt('歌曲') }}</h2>
                            <span class="text-sm text-base-content/50">{{ tracks.length }}</span>
                        </div>
                        <div class="space-y-2">
                            <button
                                v-for="(track, index) in tracks"
                                :key="track.id || `desktop-track-${index}`"
                                class="w-full flex items-center gap-4 p-3 rounded-xl hover:bg-base-200 text-left"
                                @click="playTrack(index)"
                            >
                                <img :src="track.image || '/static/dist/img/album_null.jpg'" :alt="track.title" class="w-12 h-12 rounded-lg object-cover">
                                <div class="flex-1 min-w-0">
                                    <div class="font-semibold truncate">{{ track.title }}</div>
                                    <div class="text-sm text-base-content/60 truncate">{{ track.artist }}<span v-if="track.album"> · {{ track.album }}</span></div>
                                </div>
                                <div class="text-sm text-base-content/50">{{ formatDuration(track.duration) }}</div>
                            </button>
                        </div>
                    </section>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
export default {
    name: 'LibrarySearch',
    data() {
        return {
            searchTerm: '',
            artists: [],
            albums: [],
            tracks: [],
            isLoading: false,
            searchTimeout: null
        }
    },
    computed: {
        isListenerMobile() {
            return !this.$store.state.common.isSuperuser
                && typeof window !== 'undefined'
                && window.innerWidth <= 768
        },
        hasKeyword() {
            return this.searchTerm.length > 0
        },
        showEmpty() {
            return this.hasKeyword && !this.isLoading && !this.artists.length && !this.albums.length && !this.tracks.length
        }
    },
    watch: {
        '$route.query.q'(value) {
            const keyword = typeof value === 'string' ? value.trim() : ''
            if (keyword === this.searchTerm) {
                return
            }
            this.searchTerm = keyword
            if (keyword) {
                this.fetchSearch(keyword)
            } else {
                this.resetResults()
            }
        }
    },
    mounted() {
        const keyword = typeof this.$route.query.q === 'string' ? this.$route.query.q.trim() : ''
        if (keyword) {
            this.searchTerm = keyword
            this.fetchSearch(keyword)
        }
        this.$nextTick(() => {
            const target = this.isListenerMobile ? this.$refs.mobileInput : this.$refs.desktopInput
            if (target) {
                target.focus()
            }
        })
    },
    beforeDestroy() {
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = null
        }
    },
    methods: {
        handleInput() {
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout)
            }
            if (!this.searchTerm) {
                this.resetResults()
                this.syncQuery('')
                return
            }
            this.searchTimeout = setTimeout(() => {
                this.searchTimeout = null
                this.fetchSearch(this.searchTerm)
            }, 260)
        },
        submitSearch() {
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout)
                this.searchTimeout = null
            }
            this.fetchSearch(this.searchTerm)
        },
        async fetchSearch(keyword) {
            const query = keyword.trim()
            this.syncQuery(query)
            if (!query) {
                this.resetResults()
                return
            }
            this.isLoading = true
            try {
                const result = await this.$api.subsonic.search(query, 6, 0, 8, 0, 12, 0)
                this.artists = result.artists || []
                this.albums = result.albums || []
                this.tracks = result.tracks || []
            } catch (error) {
                console.error('搜索失败:', error)
                this.resetResults()
            } finally {
                this.isLoading = false
            }
        },
        syncQuery(query) {
            const current = typeof this.$route.query.q === 'string' ? this.$route.query.q : ''
            if (current === query) {
                return
            }
            this.$router.replace({
                path: this.$route.path,
                query: query ? { q: query } : {}
            })
        },
        resetResults() {
            this.isLoading = false
            this.artists = []
            this.albums = []
            this.tracks = []
        },
        clearSearch() {
            this.searchTerm = ''
            this.resetResults()
            this.syncQuery('')
            this.$nextTick(() => {
                const target = this.isListenerMobile ? this.$refs.mobileInput : this.$refs.desktopInput
                if (target) {
                    target.focus()
                }
            })
        },
        goBack() {
            if (window.history.length > 1) {
                this.$router.back()
                return
            }
            this.$router.push({ path: '/discover' })
        },
        redirectArtist(item) {
            this.$router.push({ path: '/artist/id/' + item.id })
        },
        redirectAlbum(item) {
            this.$router.push({ path: '/album/id/' + item.id })
        },
        playTrack(index) {
            this.$store.dispatch('playTrackList', {
                index,
                tracks: this.tracks
            })
        },
        formatDuration(value) {
            if (!isFinite(value)) {
                return '∞'
            }
            const minutes = Math.floor(value / 60)
            const seconds = Math.floor(value % 60)
            return (minutes < 10 ? '0' : '') + minutes + ':' + (seconds < 10 ? '0' : '') + seconds
        }
    }
}
</script>

<style scoped>
.listener-search {
    height: calc(100dvh - 12.25rem);
    padding: 0.4rem 0.1rem 0.25rem;
    box-sizing: border-box;
    overflow-y: auto;
}

.listener-search__hero {
    padding: 0.45rem 0.3rem 0.2rem;
}

.listener-search__top {
    display: flex;
    align-items: flex-start;
    gap: 0.9rem;
    margin-bottom: 1rem;
}

.listener-search__back {
    width: 2.85rem;
    height: 2.85rem;
    border: 0;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.88);
    color: #111827;
    box-shadow: 0 12px 28px rgba(148, 163, 184, 0.18);
    flex-shrink: 0;
}

.listener-search__back svg {
    width: 1.2rem;
    height: 1.2rem;
}

.listener-search__eyebrow {
    font-size: 0.9rem;
    font-weight: 700;
    color: #94a3b8;
    letter-spacing: 0.04em;
}

.listener-search__title {
    margin-top: 0.18rem;
    font-size: 2rem;
    line-height: 1;
    font-weight: 800;
    color: #111827;
    letter-spacing: -0.04em;
}

.listener-search__bar {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.95rem 1rem;
    border-radius: 1.5rem;
    background: rgba(255, 255, 255, 0.9);
    box-shadow: 0 16px 34px rgba(148, 163, 184, 0.18);
}

.listener-search__bar > svg {
    width: 1.15rem;
    height: 1.15rem;
    color: #64748b;
    flex-shrink: 0;
}

.listener-search__bar input {
    flex: 1;
    min-width: 0;
    border: 0;
    background: transparent;
    color: #111827;
    font-size: 0.98rem;
    outline: none;
}

.listener-search__bar input::placeholder {
    color: #94a3b8;
}

.listener-search__clear {
    width: 2rem;
    height: 2rem;
    border: 0;
    border-radius: 999px;
    background: rgba(226, 232, 240, 0.72);
    color: #475569;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.listener-search__clear svg {
    width: 0.9rem;
    height: 0.9rem;
}

.listener-search__section {
    margin-top: 1rem;
    padding: 0 0.3rem;
}

.listener-search__section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.75rem;
}

.listener-search__section-header h2 {
    font-size: 1.08rem;
    font-weight: 800;
    color: #111827;
}

.listener-search__section-header span {
    font-size: 0.75rem;
    font-weight: 700;
    color: #94a3b8;
}

.listener-search__artist-list,
.listener-search__track-list {
    display: grid;
    gap: 0.75rem;
}

.listener-search__artist-card,
.listener-search__track-row {
    width: 100%;
    border: 0;
    background: rgba(255, 255, 255, 0.84);
    border-radius: 1.4rem;
    padding: 0.8rem;
    display: flex;
    align-items: center;
    gap: 0.85rem;
    text-align: left;
    box-shadow: 0 16px 30px rgba(148, 163, 184, 0.12);
}

.listener-search__artist-image,
.listener-search__track-cover {
    width: 3.5rem;
    height: 3.5rem;
    object-fit: cover;
    flex-shrink: 0;
}

.listener-search__artist-image {
    border-radius: 999px;
}

.listener-search__track-cover {
    border-radius: 1rem;
}

.listener-search__artist-meta,
.listener-search__track-meta {
    min-width: 0;
    flex: 1;
}

.listener-search__artist-name,
.listener-search__track-title,
.listener-search__album-title,
.listener-search__album-sub,
.listener-search__track-sub {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.listener-search__artist-name,
.listener-search__track-title {
    font-size: 0.98rem;
    font-weight: 700;
    color: #111827;
}

.listener-search__artist-sub,
.listener-search__track-sub,
.listener-search__album-sub {
    margin-top: 0.18rem;
    font-size: 0.76rem;
    color: #64748b;
}

.listener-search__album-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.85rem;
}

.listener-search__album-card {
    width: 100%;
    border: 0;
    background: rgba(255, 255, 255, 0.84);
    border-radius: 1.55rem;
    padding: 0.75rem;
    text-align: left;
    box-shadow: 0 16px 30px rgba(148, 163, 184, 0.12);
}

.listener-search__album-image {
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
    border-radius: 1.1rem;
    margin-bottom: 0.65rem;
}

.listener-search__album-title {
    font-size: 0.92rem;
    font-weight: 700;
    color: #111827;
}

.listener-search__track-duration {
    font-size: 0.76rem;
    font-weight: 700;
    color: #94a3b8;
    flex-shrink: 0;
}

.listener-search__state {
    padding: 3.2rem 1.3rem 1.2rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    color: #64748b;
}

.listener-search__state h2 {
    margin-top: 0.9rem;
    font-size: 1.15rem;
    font-weight: 800;
    color: #111827;
}

.listener-search__state p {
    margin-top: 0.38rem;
    font-size: 0.84rem;
    line-height: 1.5;
    max-width: 16rem;
}

.listener-search__hint-icon {
    width: 4.2rem;
    height: 4.2rem;
    border-radius: 1.6rem;
    background: rgba(255, 255, 255, 0.84);
    color: #94a3b8;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 16px 30px rgba(148, 163, 184, 0.12);
}

.listener-search__hint-icon svg {
    width: 1.6rem;
    height: 1.6rem;
}

.listener-search__loading {
    width: 2.2rem;
    height: 2.2rem;
    border-radius: 999px;
    border: 3px solid rgba(148, 163, 184, 0.22);
    border-top-color: #111827;
    animation: listener-search-spin 0.85s linear infinite;
}

@keyframes listener-search-spin {
    to {
        transform: rotate(360deg);
    }
}
</style>
